// Modo "começar zerado": a projeção começa em R$ 0 e o usuário digita o VALOR previsto de cada
// linha (conta ou categoria) no período da quebra. Tudo é guardado por categoria x mês
// (state.values); os nós/totalizadores somam as categorias, como sempre.
//
// Distribuição de um valor digitado:
// - entre os meses do período: partes iguais (o valor do ano vira 1/12 em cada mês e 1/4 em cada
//   trimestre; o do trimestre, 1/3 em cada mês);
// - entre as categorias de uma conta: proporcional à base de cada categoria no mês
//   (sem base no mês, ao peso anual; sem nenhuma base, partes iguais).
//
// Sinal: o valor segue o sinal natural da linha no DRE (despesas/deduções negativas). O usuário
// digita o valor absoluto; numa linha de natureza negativa, o valor é gravado negativo.

import { MONTH_INDEXES, zeros12 } from './periods';
import { INPUT_MODE, TARGET } from './adjustments';

const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

/**
 * Valor em R$ digitado (pt-BR): "1.500.000", "1500000", "1.234,56", "1234.5".
 * Com vírgula, pontos são milhar; sem vírgula, um único ponto seguido de 1–2 dígitos é decimal.
 * Caracteres inválidos são ignorados. Retorna null para vazio/inválido.
 */
export const parseMoneyInput = (text) => {
  if (text == null) return null;
  if (typeof text === 'number') return Number.isFinite(text) ? round2(text) : null;
  let s = String(text).replace(/[^0-9,.-]/g, '');
  const negative = s.trim().startsWith('-');
  s = s.replace(/-/g, '');
  if (s.includes(',')) {
    const i = s.lastIndexOf(',');
    s = `${s.slice(0, i).replace(/[.,]/g, '')}.${s.slice(i + 1).replace(/[.,]/g, '')}`;
  } else if (/^\d*\.\d{1,2}$/.test(s)) {
    // decimal com ponto
  } else {
    s = s.replace(/\./g, '');
  }
  if (!s || s === '.') return null;
  const value = Number(s);
  if (!Number.isFinite(value)) return null;
  return round2(negative ? -value : value);
};

const sumAbs = (arr) => arr.reduce((acc, v) => acc + Math.abs(v), 0);

/** Categorias de uma linha (nó: todas as da subárvore; categoria: ela mesma). */
export const rowCategories = (model, targetType, key) => {
  if (targetType === TARGET.CATEGORY) return model.categories.has(key) ? [key] : [];
  return model.nodeById.get(key)?.allCats || [];
};

/**
 * Sinal natural da linha: receita (+) ou despesa/dedução (−), pela base; sem base, pela natureza
 * (Receita Bruta do DRE; fora da DRE, a natureza do cadastro do ERP).
 */
export const naturalSign = (model, targetType, key) => {
  const cats = rowCategories(model, targetType, key);
  let total = 0;
  cats.forEach((cod) => { total += (model.base.get(cod) || []).reduce((a, b) => a + b, 0); });
  if (total > 0.005) return 1;
  if (total < -0.005) return -1;
  const isRevenue = (c) => model.revenueCats.has(c) || Boolean(model.outsideRevenueCats?.has(c));
  return cats.length && cats.every(isRevenue) ? 1 : -1;
};

/** Mês sem base: valor da base zerado (|v| < 0,005). */
export const isZeroBaseMonth = (base, m) => Math.abs(base?.[m] || 0) < 0.005;

/** Célula sem base: todas as categorias da linha estão zeradas em todos os meses do período. */
export const isZeroBaseCell = (model, targetType, key, months = MONTH_INDEXES) => {
  const cats = rowCategories(model, targetType, key);
  return cats.length > 0
    && cats.every((cod) => months.every((m) => isZeroBaseMonth(model.base.get(cod), m)));
};

/**
 * A célula (linha × período) recebe VALOR (R$) em vez de %? No início zerado, sempre. No
 * pré-preenchido, a célula sem base segue a regra do orçamento base zero (% sobre zero não projeta
 * nada). Os demais períodos da mesma linha continuam em %.
 */
export const isValueCell = (model, state, targetType, key, months = MONTH_INDEXES) => {
  if (state?.mode === INPUT_MODE.VALUE) return true;
  return isZeroBaseCell(model, targetType, key, months);
};

/** Valor projetado (soma) de uma linha nos meses informados, no modo valor. */
export const rowValue = (state, cats, months = MONTH_INDEXES) => {
  let total = 0;
  cats.forEach((cod) => {
    const values = state.values?.[cod];
    if (!values) return;
    months.forEach((m) => { total += values[m] || 0; });
  });
  return total;
};

const weights = (raw) => {
  const total = sumAbs(raw);
  if (total > 0) return raw.map((v) => Math.abs(v) / total);
  return raw.map(() => 1 / raw.length);
};

/**
 * Divide `amount` (já com sinal) em partes iguais entre os meses do período e, em cada mês, entre as
 * categorias (peso da base no mês; sem base no mês, peso anual; sem nenhuma base, partes iguais).
 * Map(cod -> number[12]), só os meses do período preenchidos.
 */
const distributeAmount = (model, cats, months, amount) => {
  const out = new Map(cats.map((cod) => [cod, zeros12()]));
  // Arredondamento acumulado: cada mês = acumulado até ele − acumulado até o anterior, para o total
  // (e cada trimestre, no valor do ano) fechar exato no centavo.
  const cumulative = (i) => round2((amount * i) / months.length);

  months.forEach((m, i) => {
    const monthTotal = round2(cumulative(i + 1) - cumulative(i));
    let catRaw = cats.map((cod) => (model.base.get(cod) || [])[m] || 0);
    if (sumAbs(catRaw) === 0) catRaw = cats.map((cod) => (model.base.get(cod) || []).reduce((a, b) => a + Math.abs(b), 0));
    const catWeights = weights(catRaw);
    cats.forEach((cod, j) => { out.get(cod)[m] = round2(monthTotal * catWeights[j]); });

    // Ajusta o arredondamento entre as categorias na maior do mês, para o mês bater exatamente.
    const diff = round2(monthTotal - cats.reduce((acc, cod) => acc + out.get(cod)[m], 0));
    if (diff) {
      const target = cats.reduce((best, cod) => (Math.abs(out.get(cod)[m]) >= Math.abs(out.get(best)[m]) ? cod : best), cats[0]);
      out.get(target)[m] = round2(out.get(target)[m] + diff);
    }
  });
  return out;
};

const writeValues = (model, state, cats, months, amount, add) => {
  if (!cats.length) return state;
  const periodMonths = months && months.length ? months : MONTH_INDEXES;
  const dist = distributeAmount(model, cats, periodMonths, amount);
  const nextValues = { ...(state.values || {}) };
  cats.forEach((cod) => {
    const series = nextValues[cod] ? [...nextValues[cod]] : zeros12();
    periodMonths.forEach((m) => { series[m] = round2((add ? series[m] : 0) + dist.get(cod)[m]); });
    if (series.every((v) => !v)) delete nextValues[cod];
    else nextValues[cod] = series;
  });
  return { ...state, values: nextValues };
};

/** Grava o total `amount` (já com sinal) das categorias no período, sobrescrevendo esses meses. */
export const setCategoriesValue = (model, state, cats, months, amount) => writeValues(model, state, cats, months, amount, false);

/** Soma `amount` (já com sinal) ao que já foi lançado nas categorias no período. */
export const addCategoriesValue = (model, state, cats, months, amount) => writeValues(model, state, cats, months, amount, true);

/**
 * Grava o valor `amount` (já com sinal) de uma linha no período `months`, sobrescrevendo as
 * categorias da linha nesses meses.
 */
export const setRowValue = (model, state, targetType, key, months, amount) =>
  setCategoriesValue(model, state, rowCategories(model, targetType, key), months, amount);

/** Projeção do modo valor: o que foi digitado (categorias sem valor = 0). */
export const projectValues = (model, state) => {
  const projected = new Map();
  model.catList.forEach((cod) => {
    const values = state.values?.[cod];
    projected.set(cod, values ? [...values] : zeros12());
  });
  return projected;
};
